// Copyright 2026 L3montree GmbH and the DevGuard Contributors.
// SPDX-License-Identifier: 	AGPL-3.0-or-later

import { UserRole } from "@/types/view/vuln";

import { PackageURL } from "packageurl-js";
import type { DependencyVuln } from "@/types/dto";

export function classNames(...classes: Array<string | undefined | Boolean>) {
  return classes.filter(Boolean).join(" ");
}

export const getEcosystem = (packageName?: string) => {
  if (!packageName) {
    return "";
  }

  if (packageName.startsWith("pkg:")) {
    packageName = packageName.split(":")[1].split("/")[0];
  } else if (packageName.includes("/")) {
    packageName = packageName.split("/")[0];
  }

  return packageName;
};

export const isNumber = (v: any): v is number => {
  if (v === null || v === undefined) {
    return false;
  }

  // checks for NaN
  return typeof v === "number" && v === v;
};

export const getVulnerabilitySourceUrl = (vulnerabilityId: string): string =>
  vulnerabilityId.startsWith("EUVD-")
    ? `https://euvd.enisa.europa.eu/enisa/${vulnerabilityId}`
    : `https://osv.dev/vulnerability/${vulnerabilityId}`;

export const beautifyPurl = (purl: string): string => {
  if (purl === "ROOT") {
    return "Your application";
  }
  if (!purl) {
    return "";
  }
  try {
    const p = PackageURL.fromString(purl);
    return p.namespace ? `${p.namespace}/${p.name}` : p.name;
  } catch {
    return purl;
  }
};

export const extractVersion = (purl: string): string => {
  if (!purl) return "";
  try {
    return PackageURL.fromString(purl).version ?? "";
  } catch {
    return "";
  }
};

// Artifact names are already valid purls (e.g. "pkg:oci/my-app") - this just
// fills in the version, since the asset itself carries no version, only its
// refs (branches/tags) do.
export const purlWithVersion = (purl: string, version: string): string => {
  if (!purl) return purl;
  try {
    const p = PackageURL.fromString(purl);
    p.version = version;
    return p.toString();
  } catch {
    return purl;
  }
};

export const extractPurlQualifiers = (purl: string) => {
  const [, qualifiersPart] = purl.split("?", 2);
  if (!qualifiersPart) {
    return "";
  }

  return qualifiersPart.split("&").join(", ");
};

export const isValidPackagePurl = (purl: string): boolean => {
  if (!purl) return false;
  // PURL format: pkg:<type>/<namespace?>/<name>@<version?>?<qualifiers?>#<subpath?>
  // Type: lowercase alphanumeric with +, ., -
  // Name/namespace: alphanumeric with ., _, -, %, ~, @, /
  const purlRegex =
    /^pkg:[a-z][a-z0-9+.-]*\/[a-zA-Z0-9._~%@/-]+[a-zA-Z0-9](?:[@?#].*)?$/;
  return purlRegex.test(purl);
};

export const validateArtifactNameAgainstPurlSpec = (
  artifactName: string,
): { isValid: boolean; warning?: string } => {
  if (!artifactName) {
    return { isValid: true };
  }

  // Artifact name should be a PURL without version
  if (!artifactName.startsWith("pkg:")) {
    return {
      isValid: false,
      warning:
        "Your artifact name is not a valid PURL. This makes it hard for your downstream consumers to match vulnerabilities against your application name. A valid PURL looks like: pkg:npm/express or pkg:maven/org.apache/commons-lang3",
    };
  }

  // Try to construct a full PURL with the version
  // Replace any existing version in the artifact name with the new one
  let testPurl = artifactName;

  // If artifact name already contains a version (@), replace it
  const atIndex = testPurl.lastIndexOf("@");
  const hashIndex = testPurl.indexOf("#");
  const questionIndex = testPurl.indexOf("?");

  if (
    atIndex !== -1 &&
    (hashIndex === -1 || atIndex > hashIndex) &&
    (questionIndex === -1 || atIndex > questionIndex)
  ) {
    // There's a version after the last @, replace it
    testPurl = testPurl.substring(0, atIndex);
  }

  testPurl = `${testPurl}@1.0.0`;

  // Validate the constructed PURL
  if (!isValidPackagePurl(testPurl)) {
    return {
      isValid: false,
      warning:
        "Your artifact name is not a valid PURL. This makes it hard for your downstream consumers to match vulnerabilities against your application name. PURL format: pkg:<type>/<namespace>/<name> (e.g., pkg:npm/express, pkg:maven/org.apache/commons-lang3)",
    };
  }

  return { isValid: true };
};

export function allowedActionsCheck(
  currentUserRole: UserRole | null,
  memberRole?: UserRole | string,
): boolean {
  if (!memberRole) {
    return false;
  }

  if (currentUserRole === UserRole.Owner) {
    return true;
  }

  if (
    currentUserRole === UserRole.Admin &&
    (memberRole === UserRole.Member || memberRole === UserRole.Admin)
  ) {
    return true;
  }

  return false;
}

export const licenses = [
  { value: "bsd-1-clause", label: "BSD-1-Clause" },
  { value: "afl-3.0", label: "AFL-3.0" },
  { value: "apl-1.0", label: "APL-1.0" },
  { value: "apache-1.1", label: "Apache-1.1" },
  { value: "apache-2.0", label: "Apache-2.0" },
  { value: "apsl-2.0", label: "APSL-2.0" },
  { value: "artistic-1.0-perl", label: "Artistic-1.0-Perl" },
  { value: "artistic-1.0", label: "Artistic-1.0" },
  { value: "artistic-2.0", label: "Artistic-2.0" },
  { value: "aal", label: "AAL" },
  { value: "blueoak-1.0.0", label: "BlueOak-1.0.0" },
  { value: "bsl-1.0", label: "BSL-1.0" },
  { value: "bsd-2-clause-patent", label: "BSD-2-Clause-Patent" },
  { value: "cecill-2.1", label: "CECILL-2.1" },
  { value: "cern-ohl-p-2.0", label: "CERN-OHL-P-2.0" },
  { value: "cern-ohl-s-2.0", label: "CERN-OHL-S-2.0" },
  { value: "cern-ohl-w-2.0", label: "CERN-OHL-W-2.0" },
  { value: "mit-cmu", label: "MIT-CMU" },
  { value: "cddl-1.0", label: "CDDL-1.0" },
  { value: "cpal-1.0", label: "CPAL-1.0" },
  { value: "cpl-1.0", label: "CPL-1.0" },
  { value: "catosl-1.1", label: "CATOSL-1.1" },
  { value: "cal-1.0", label: "CAL-1.0" },
  { value: "cua-opl-1.0", label: "CUA-OPL-1.0" },
  { value: "epl-1.0", label: "EPL-1.0" },
  { value: "epl-2.0", label: "EPL-2.0" },
  { value: "ecos-2.0", label: "eCos-2.0" },
  { value: "ecl-1.0", label: "ECL-1.0" },
  { value: "ecl-2.0", label: "ECL-2.0" },
  { value: "efl-1.0", label: "EFL-1.0" },
  { value: "efl-2.0", label: "EFL-2.0" },
  { value: "entessa", label: "Entessa" },
  { value: "eudatagrid", label: "EUDatagrid" },
  { value: "eupl-1.2", label: "EUPL-1.2" },
  { value: "fair", label: "Fair" },
  { value: "frameworx-1.0", label: "Frameworx-1.0" },
  { value: "agpl-3.0-only", label: "AGPL-3.0-only" },
  { value: "gpl-2.0", label: "GPL-2.0" },
  { value: "gpl-3.0-only", label: "GPL-3.0-only" },
  { value: "lgpl-2.1", label: "LGPL-2.1" },
  { value: "lgpl-3.0-only", label: "LGPL-3.0-only" },
  { value: "lgpl-2.0-only", label: "LGPL-2.0-only" },
  { value: "hpnd", label: "HPND" },
  { value: "ipl-1.0", label: "IPL-1.0" },
  { value: "icu", label: "ICU" },
  { value: "intel", label: "Intel" },
  { value: "ipa", label: "IPA" },
  { value: "isc", label: "ISC" },
  { value: "jam", label: "Jam" },
  { value: "lppl-1.3c", label: "LPPL-1.3c" },
  { value: "bsd-3-clause-lbnl", label: "BSD-3-Clause-LBNL" },
  { value: "liliq-p-1.1", label: "LiLiQ-P-1.1" },
  { value: "liliq-rplus-1.1", label: "LiLiQ-Rplus-1.1" },
  { value: "liliq-r-1.1", label: "LiLiQ-R-1.1" },
  { value: "lpl-1.02", label: "LPL-1.02" },
  { value: "lpl-1.0", label: "LPL-1.0" },
  { value: "ms-pl", label: "MS-PL" },
  { value: "ms-rl", label: "MS-RL" },
  { value: "miros", label: "MirOS" },
  { value: "mit-0", label: "MIT-0" },
  { value: "mit", label: "MIT" },
  { value: "motosoto", label: "Motosoto" },
  { value: "mpl-1.1", label: "MPL-1.1" },
  { value: "mpl-2.0", label: "MPL-2.0" },
  { value: "mpl-1.0", label: "MPL-1.0" },
  { value: "mulanpsl-2.0", label: "MulanPSL-2.0" },
  { value: "multics", label: "Multics" },
  { value: "nasa-1.3", label: "NASA-1.3" },
  { value: "naumen", label: "Naumen" },
  { value: "nokia", label: "NOKIA" },
  { value: "nposl-3.0", label: "NPOSL-3.0" },
  { value: "ntp", label: "NTP" },
  { value: "ogtsl", label: "OGTSL" },
  { value: "olfl-1.3", label: "OLFL-1.3" },
  { value: "osl-2.1", label: "OSL-2.1" },
  { value: "osl-1.0", label: "OSL-1.0" },
  { value: "oldap-2.8", label: "OLDAP-2.8" },
  { value: "oset-pl-2.1", label: "OSET-PL-2.1" },
  { value: "php-3.0", label: "PHP-3.0" },
  { value: "php-3.01", label: "PHP-3.01" },
  { value: "psf-2.0", label: "PSF-2.0" },
  { value: "rpsl-1.0", label: "RPSL-1.0" },
  { value: "rpl-1.5", label: "RPL-1.5" },
  { value: "rpl-1.1", label: "RPL-1.1" },
  { value: "ofl-1.1", label: "OFL-1.1" },
  { value: "simpl-2.0", label: "SimPL-2.0" },
  { value: "sissl", label: "SISSL" },
  { value: "spl-1.0", label: "SPL-1.0" },
  { value: "bsd-3-clause", label: "BSD-3-Clause" },
  { value: "cnri-python", label: "CNRI-Python" },
  { value: "eupl-1.1", label: "EUPL-1.1" },
  { value: "ngpl", label: "NGPL" },
  { value: "osl-3.0", label: "OSL-3.0" },
  { value: "postgresql", label: "PostgreSQL" },
  { value: "qpl-1.0", label: "QPL-1.0" },
  { value: "rscpl", label: "RSCPL" },
  { value: "sleepycat", label: "Sleepycat" },
  { value: "watcom-1.0", label: "Watcom-1.0" },
  { value: "upl-1.0", label: "UPL-1.0" },
  { value: "ncsa", label: "NCSA" },
  { value: "unlicense", label: "Unlicense" },
  { value: "vsl-0.1", label: "VSL-0.1" },
  { value: "w3c-20150513", label: "W3C-20150513" },
  { value: "wxwindows", label: "wxWindows" },
  { value: "xnet", label: "Xnet" },
  { value: "zlib", label: "Zlib" },
  { value: "unicode-dfs-2015", label: "Unicode-DFS-2015" },
  { value: "ucl-1.0", label: "UCL-1.0" },
  { value: "zpl-2.0", label: "ZPL-2.0" },
  { value: "zpl-2.1", label: "ZPL-2.1" },
];

const regexpNonAuthorizedChars = /[^a-zA-Z0-9-_]/g;
const regexpMultipleDashes = /-+/g;

export const ensureValidBranchOrTagSlug = (name: string) => {
  return name
    .replaceAll(regexpNonAuthorizedChars, "-")
    .replaceAll(regexpMultipleDashes, "-");
};

export const stateLabels: Record<DependencyVuln["state"], string> = {
  open: "Open",
  fixed: "Fixed",
  accepted: "Accepted",
  falsePositive: "False Positive",
  markedForTransfer: "Marked for Transfer",
  implemented: "Implemented",
  notApplicable: "Not Applicable",
  draft: "Draft",
  public: "Published",
  withdrawn: "Withdrawn",
};

// Utility function to truncate text in the middle with ellipsis
export const truncateMiddle = (
  text: string,
  maxLength: number = 20,
): string => {
  if (text.length <= maxLength) return text;

  const start = Math.ceil((maxLength - 3) / 2);
  const end = Math.floor((maxLength - 3) / 2);

  return text.slice(0, start) + "..." + text.slice(-end);
};

export const formatPurlQualifiers = (purl: string, maxLength = 48) =>
  truncateMiddle(extractPurlQualifiers(purl), maxLength);

// round any number to its 2nd digit using math round
export function roundToSecondDigit(num?: number): number {
  if (!num) {
    return 0;
  }
  return Math.round(100 * num) / 100;
}

export function getHumanReadableDuration(seconds: number): {
  duration: string;
  type: string;
} {
  const timeUnits = [
    { unit: "year", seconds: 365 * 24 * 60 * 60 },
    { unit: "month", seconds: 30 * 24 * 60 * 60 },
    { unit: "week", seconds: 7 * 24 * 60 * 60 },
    { unit: "day", seconds: 24 * 60 * 60 },
    { unit: "hour", seconds: 60 * 60 },
    { unit: "minute", seconds: 60 },
    { unit: "second", seconds: 1 },
  ];

  for (let i = 0; i < timeUnits.length; i++) {
    const currentUnit = timeUnits[i];
    if (seconds >= currentUnit.seconds) {
      const duration = seconds / currentUnit.seconds;
      return {
        duration: duration.toFixed(2),
        type: currentUnit.unit + (duration > 1 ? "s" : ""),
      };
    }
  }

  // If the input is less than 1 second
  return {
    duration: (0).toFixed(2),
    type: "seconds",
  };
}
